import React, { useState } from 'react';
import PropTypes from 'prop-types';

import GridInput from "../../ui/input/GridInput";
import ValidationText from '../../ui/validation/ValidationText';

import { TextInputType } from '../../libs/data/formControlTypes';

/**
 * EditInputColumn component for rendering an input field with editable functionality.
 * @param {object} props - Component props.
 * @param {object} props.rowData - Data for the current row.
 * @param {object} props.col - Column configuration.
 * @param {object} props.editColumn - Editable column configuration.
 * @param {number} props.rowIndex - Index of the current row.
 * @param {function} props.onChange - Function to handle changes.
 * @param {object} props.updatedData - Updated row data.
 * @param {object} props.errors - Validation errors.
 */
const EditInputColumn = ({
    rowData,
    col,
    editColumn,
    rowIndex,
    onChange,
    onBlur,
    updatedData,
    errors,
}) => {

    const [value, setValue] = useState(rowData[col.fieldName]);

    const {
        isDisable,
        editColType,
        placeholder,
        colConfig = {},// Destructure colConfig with a default value of an empty object
    } = editColumn;

    /**
     * Handles change in input value.
     * @param {object} e - Event object from input change.
     */
    const handleOnChange = (e) => {
        const newValue = e.target.value;
        setValue(newValue);

        if (onChange) {
            const newRowData = updatedData
                ? { ...updatedData, [col.fieldName]: newValue }
                : { ...rowData, [col.fieldName]: newValue };
            onChange(rowIndex, newRowData, col?.fieldName);
        }
    };

    /**
     * Handles blur event of input value (field loses focus).
     * @param {object} e - Event object from input blur.
     */
    const handleBlur = (e) => {
        const newValue = e.target.value;
        setValue(newValue); // Update the state with the new value

        // Handle validation and data update
        if (onBlur) {
            const newRowData = updatedData
                ? { ...updatedData, [col.fieldName]: newValue }
                : { ...rowData, [col.fieldName]: newValue };


            // Call the provided onBlur function with necessary data
            onBlur(rowIndex, newRowData, col?.fieldName);
        }
    };


    const hasError = !!errors?.[rowIndex]?.[col.fieldName];
    const errorMessage = errors?.[rowIndex]?.[col.fieldName] || '';

    return (
        <>
            <GridInput
                type={editColType && editColType === 3 ? TextInputType.NUMBER : TextInputType.TEXT}
                placeholder={placeholder}
                value={value}
                min={colConfig?.min}
                max={colConfig?.max}
                onChange={handleOnChange}
                onBlur={handleBlur}
                minLength={colConfig?.minLength}
                maxLength={colConfig?.maxLength}
                cssClass={`input-field ${hasError ? 'error' : ''}`}
                allowSpace={true}
                isDisable={isDisable}
                isReadOnly={colConfig?.isReadOnly}
                hasError={hasError}
            />
            {hasError && <ValidationText error={errorMessage || ' '} className="error-message" />}
        </>
    );
};

EditInputColumn.propTypes = {
    rowData: PropTypes.object.isRequired,
    col: PropTypes.shape({
        fieldName: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
    }).isRequired,
    editColumn: PropTypes.shape({
        editColType: PropTypes.number,
        isDisable: PropTypes.bool,
        placeholder: PropTypes.string,
        colConfig: PropTypes.shape({
            min: PropTypes.number,
            max: PropTypes.number,
            minLength: PropTypes.number,
            maxLength: PropTypes.number,
            isReadOnly: PropTypes.bool,
        }),
    }).isRequired,
    rowIndex: PropTypes.number.isRequired,
    onChange: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    updatedData: PropTypes.object,
    errors: PropTypes.objectOf(
        PropTypes.objectOf(PropTypes.string)
    ),
};

export default EditInputColumn;